import { formatKm } from '../distance';
import type { DailySummary, NarrativeLog, TripCandidate } from '../models';
import type { NarrativeProvider } from './NarrativeProvider';

export class TemplateNarrativeProvider implements NarrativeProvider {
  id = 'template';
  label = 'ルールベーステンプレート';

  async isAvailable(): Promise<boolean> {
    return true;
  }

  async generateDailyLog(input: DailySummary): Promise<NarrativeLog> {
    return createLog('daily', renderDailyLog(input), stableHash(input));
  }

  async generateTripLog(input: TripCandidate): Promise<NarrativeLog> {
    return createLog('trip', renderTripLog(input), stableHash(input));
  }
}

export function renderDailyLog(input: DailySummary): string {
  const firstArea = input.topVisits[0]
    ? `${input.topVisits[0].center.lat.toFixed(2)}, ${input.topVisits[0].center.lng.toFixed(2)} 周辺`
    : '記録開始地点';
  const topVisits =
    input.topVisits.length > 0
      ? input.topVisits
          .slice(0, 3)
          .map((visit) => `${visit.placeName ?? `${visit.center.lat.toFixed(2)}, ${visit.center.lng.toFixed(2)} 周辺`}（約 ${Math.round(visit.durationMinutes)} 分）`)
          .join('、')
      : '滞在候補なし';

  return [
    `## ${input.date} の日次ログ`,
    '',
    `${input.date} は ${firstArea} から行動を開始し、訪問候補は ${input.visitCount} 件でした。`,
    `推定移動距離は ${formatKm(input.distanceMeters)} km、外出時間は約 ${(input.activeMinutes / 60).toFixed(1)} 時間です。`,
    `主な滞在候補は ${topVisits} です。`,
    '',
    `信頼度: ${Math.round(input.confidence * 100)}%`,
  ].join('\n');
}

export function renderTripLog(input: TripCandidate): string {
  const areas = input.mainAreas.map((area) => `${area.label}（${area.days}日）`).join('、') || '主なエリア未検出';
  const reasons = input.reason.map((reason) => `- ${reason}`).join('\n');

  return [
    `## ${input.title}`,
    '',
    `${input.startDate} から ${input.endDate} にかけて、旅行/出張/遠征の候補が検出されました。`,
    `基準地点から最大 ${input.homeDistanceKmMax.toFixed(0)} km 離れ、総移動距離は約 ${input.totalDistanceKm.toFixed(1)} km です。`,
    `主な滞在エリアは ${areas} です。`,
    '',
    '### 判定理由',
    reasons || '- 判定理由は少なめです',
    '',
    `信頼度: ${Math.round(input.confidence * 100)}%`,
  ].join('\n');
}

function createLog(type: NarrativeLog['type'], markdown: string, inputHash: string): NarrativeLog {
  return {
    id: `log_${type}_${Date.now().toString(36)}`,
    type,
    generatedBy: 'template',
    inputHash,
    markdown,
    warnings: ['この文章は位置情報からの推定に基づく下書きです'],
    createdAt: new Date().toISOString(),
  };
}

function stableHash(value: unknown): string {
  const input = JSON.stringify(value);
  let hash = 0;
  for (let index = 0; index < input.length; index += 1) {
    hash = (hash * 31 + input.charCodeAt(index)) | 0;
  }
  return Math.abs(hash).toString(16);
}
